import { useTranslation } from 'react-i18next';

import { CheckIcon, EnvelopeOpenIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

import { Image } from '~/components/Image';
import Phone from '~/components/Phone';
import Text from '~/components/Text';
import type { Publication } from '~/interfaces/publication';

interface Props {
  publication: Publication;
  publications: Publication[];
}

const PhoneLineItem = ({ text, isFiller = false }: { text: string; isFiller?: boolean }) => {
  return (
    <div className="flex items-center space-x-3 px-4 pb-1 pt-3">
      <div className="rounded border border-gray-100/20 p-0.5">
        <CheckIcon className="h-3 w-3 text-green-500" />
      </div>
      {isFiller ? (
        <div className="h-3 w-full rounded-sm bg-gray-700" />
      ) : (
        <div className="truncate text-xs font-bold text-gray-100">{text}</div>
      )}
    </div>
  );
};

const InboxPhone = ({ publication, publications }: Props) => {
  const { t } = useTranslation();

  const publicationsCount = publications.length + 1;

  return (
    <>
      <div className="flex flex-col items-center space-y-4 sm:hidden">
        <div className="h-14 w-14 overflow-hidden rounded border border-gray-300 bg-white sm:h-32 sm:w-32">
          <Image maxWidth={128} aspectRatio={'square'} src={publication.logo.url} alt={publication.name} />
        </div>
        <Text
          as="p"
          type="title"
          size="lg"
          theme={'default'}
          className="font-regular w-full max-w-sm pb-6 text-center text-black"
        >
          {t('publications.description')} {publication.name}
        </Text>
      </div>

      <Phone className="bg-gray-900">
        <div className="w-full py-8">
          <div className="flex w-full items-center space-x-2 px-4 pb-5 pt-4">
            <div className="relative -mt-0.5">
              <EnvelopeOpenIcon className="h-6 w-6 text-gray-500" />
              <div className="absolute -left-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-gray-500 p-0.5 text-xs">
                {publicationsCount}
              </div>
            </div>
            <div className="flex h-6 w-full items-center justify-between rounded-lg border border-gray-100/20 px-2 text-xs text-gray-400">
              <span>{t('shared.cta.search')}</span> <MagnifyingGlassIcon className="text-fill h-3 w-3" />
            </div>
          </div>

          <div className="space-y-2 divide-y divide-gray-50 divide-opacity-20 border-b border-gray-100/20 pb-2">
            <PhoneLineItem text={publication.name} />
            {publications.map((pub: Publication) => (
              <PhoneLineItem key={pub.id} text={pub.name} />
            ))}
            <PhoneLineItem text={''} isFiller />
            <PhoneLineItem text={''} isFiller />
          </div>
        </div>
      </Phone>
    </>
  );
};

export default InboxPhone;
